import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Grid } from "@mui/material";
import BarChartWidgetNew from "components/Widgets/BarchartWidgetNew.tsx";
import ProgressChartWidgetNew from "components/Widgets/ProgressChartWidgetNew";
import { formatNumber } from "utils/common-util";
import { exportCostSummaryTableData, fetchResourceCostSummaryKpi } from "service/service";
import { CellExpanderFormatter } from "components/cell_expander_formatter";
import { CustomizedDataGrid } from "components/customized_data_grid";
import LoaderComponent from "components/Loader";
import CostQueryBuilder from "components/QueryBuilder/CostIndex";
import { getOperators, inputFieldType } from "components/QueryBuilder/utilities";
import ShowInfoWidget from "components/Widgets/ShowInfoWidget";
import eventEmitter from "SnackBarEventEmitter";
import RowTypography from "components/Table/RowTypography";
import PageWrapper from "components/Common/PageWraper";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from '@mui/material/styles';
import WidgetCardContainer from "components/Widgets/WidgetCardContainer";
import DetailedViewWidget from "components/Widgets/DetailedViewWidget";
import PageHeader from "components/Header/Page";
import { useFinOpsTab } from 'Context/contexts/FinOpsTabContext';
import DashboardIcon from 'assets/material-symbols_dashboard.svg';
import HierarchyWidget from "components/Widgets/HierarchyWidget";
import { useHeader } from "Context/contexts/HeaderTabRightContentContext";
import SingleSelectFilter from "components/Dropdown/SingleSelectFilter";

interface SeriesData {
  name: string;
  truncatedName: string;
  dataKey: string;
  color: string;
}

const seriesColor = [
  '#5470c6',
  '#a2d9ce',
  '#fac858',
  '#ee6666',
  '#73c0de',
  '#3ba272',
  '#fc8452',
  '#9a60b4',
  '#ea7ccc',
  '#4a90E2',
  '#91cc75',
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`costsummary-tabpanel-${index}`}
      aria-labelledby={`costsummary-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `costsummary-tab-${index}`,
    'aria-controls': `costsummary-tabpanel-${index}`,
  };
}

function resolveSearchFilter(
  isFirstWidgetFlg: boolean,
  costTrendToggle: string,
  customCostTrendToggle: string
) {
  if (isFirstWidgetFlg) {
    return costTrendToggle;
  }
  return customCostTrendToggle === 'technology'
    ? 'consumedservice'
    : customCostTrendToggle;
}

function resolveSearchText(seriesName: string, searchTextByChart: string) {
  if (seriesName) {
    return seriesName === 'Untagged' ? '' : seriesName;
  }
  if (searchTextByChart && searchTextByChart !== 'Untagged') {
    return searchTextByChart;
  }
  return '';
}

function handleApiError(error: any) {
  console.error(error);
  if (typeof error === "object" && error !== null && "code" in error && error?.code === 'ERR_CANCELED') {
    console.warn('Request was canceled by user');
    eventEmitter?.emit("showSnackbar", "Request was canceled", "info");
    return;
  }
  if (typeof error === "object" && error !== null && "response" in error) {
    const err = error as { response: { status: number } };
    if (err.response.status === 503)
      eventEmitter.emit("showSnackbar", "Service temporarily unavailable. Please try again later.", "info");
    else if (err.response.status === 504) {
      eventEmitter.emit("showSnackbar", "Gateway Timeout", "info");
    }
  }
}
interface Row {
  programname: string;
  sector: string;
  project: string;
  consumedservice: string;
  totalCost: any,
  isExpanded?: boolean;
  isExpandedChild?: boolean;
  consumedservices: [];
  children: Row[];
  [key: string]: any;
}
type CostTrendToggle = 'program' | 'sector' | 'project';


// Custom header with toggle
const ToggleHeader = <T extends string>({
  options,
  value,
  setValue,
  colors
}: {
  options: { label: string; value: T }[];
  value: T;
  setValue: (val: T) => void;
  colors: any
}) => (
  <Box
    sx={{
      position: 'static',
      zIndex: 2,
      display: 'flex',
      flexDirection: 'row',
      gap: { xs: 1, sm: 3 },
      mb: { xs: 1, sm: 0 },
    }}
  >
    {options.map(opt => (
      <Typography
        key={opt.value}
        variant="body2"
        sx={{
          color: value === opt.value ? colors.widget.tab.active.color : colors.widget.tab.inActive.color,
          cursor: "pointer",
          fontWeight: 500,
          borderBottom: value === opt.value ? `4px solid ${colors.widget.tab.active.color}` : "none",
          transition: "color 0.2s, border-bottom 0.2s"
        }}
        onClick={() => setValue(opt.value)}
      >
        {opt.label}
      </Typography>
    ))}
  </Box>
);

function getLegendFontSize(hasTruncated: boolean, containerWidth: number): number {
  if (hasTruncated) {
    if (containerWidth < 406) return 5;
    if (containerWidth < 437) return 6;
    if (containerWidth < 478) return 8;
    if (containerWidth < 515) return 10;
    return 12;
  }
  return containerWidth < 770 ? 10 : 12;
}

function getLegendItemGap(hasTruncated: boolean, containerWidth: number): number {
  if (hasTruncated) {
    if (containerWidth < 390) return 0;
    if (containerWidth < 437) return 1;
    if (containerWidth < 450) return 2;
    return 10;
  }
  return 10;
}

// Inline Azure Cloud Cost Bar Chart Components
const SharedLegend = ({
  seriesData,
  visibleSeries,
  onToggle,
  legendLabel = "",
  containerWidth = window.innerWidth,
  colors
}: {
  seriesData: SeriesData[];
  visibleSeries: string[];
  onToggle: (name: string) => void;
  legendLabel?: string;
  containerWidth?: number;
  colors?: any
}) => {
  const hasTruncated = seriesData.some(s => s.truncatedName);
  const fontSize = getLegendFontSize(hasTruncated, containerWidth);
  const itemGap = getLegendItemGap(hasTruncated, containerWidth);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        width: '100%',
        mt: 2,
        mb: 1,
        gap: 2,
      }}
    >
      {/* Legend label on the left, never wraps */}
      {legendLabel && (
        <Typography
          sx={{
            color: colors.widget.chart.legendTextColor,
            fontFamily: 'Montserrat',
            fontWeight: 'bold',
            fontSize: `${fontSize + 2}px`,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            mr: 1,
            minWidth: '80px',
          }}
        >
          {legendLabel}
        </Typography>
      )}
      {/* Legend items, wrap only under first item */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: `${itemGap}px`,
          alignItems: 'center',
          flex: 1,
          minWidth: 0,
        }}
      >
        {seriesData.map((series: SeriesData) => (
          <Box
            key={series.name}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              opacity: visibleSeries.includes(series.name) ? 1 : 0.4,
              cursor: 'pointer'
            }}
            onClick={() => onToggle(series.name)}
          >
            <Box sx={{ width: 16, height: 16, bgcolor: series.color, borderRadius: '4px' }} />
            <Typography
              variant="body2"
              sx={{
                color: colors.widget.chart.legendTextColor,
                fontSize: `${fontSize + 2}px`,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontFamily: 'Montserrat',
                whiteSpace: 'nowrap',
                maxWidth: 160,
              }}
              title={series.name}
            >
              {series.truncatedName ? series.truncatedName : series.name}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

type ResourceNameCellProps = {
  row: any;
  isCellSelected: boolean;
  queryPreference: string;
  mainColumn: any;
  toggleSubRow: (id: any, values: any) => void;
};

export const ResourceNameCell = ({
  row,
  isCellSelected,
  queryPreference,
  mainColumn,
  toggleSubRow,
}: ResourceNameCellProps) => {
  const hasChildren = Array.isArray(row.values) && row.values.length > 0;
  const indentLevel = row.level || 0;
  const indentSpace = indentLevel * 16;

  const isClickable =
    queryPreference === 'flatlist_programname' ||
    queryPreference === 'flatlist_project' ||
    (row.label &&
      (row.label.toLowerCase() === 'programname' ||
        row.label.toLowerCase() === 'project'));

  let linkPath = '';
  const prgName = row.programname || row.name || '';
  const prgId = row.programId || '';
  const prjName = row.project || row.name || '';
  const prjId = row.projectId || '';

  if (
    queryPreference === 'flatlist_programname' ||
    (row.label && row.label.toLowerCase() === 'programname')
  ) {
    linkPath = `/finops/resourcemapping/programs/${prgId}/${encodeURIComponent(prgName)}`;
  } else if (
    queryPreference === 'flatlist_project' ||
    (row.label && row.label.toLowerCase() === 'project')
  ) {
    linkPath = `/finops/resourcemapping/projects/${prgId}/${prjId}/${encodeURIComponent(
      prgName
    )}/${encodeURIComponent(prjName)}`;
  }

  const displayValue =
    (row.name && row[mainColumn.name]) ? row.name : (row[mainColumn.name] || row.name);

  return (
    <Box display="flex">
      <Box
        display="flex"
        alignItems="center"
        ml={`${indentSpace}px`}
        flexGrow={1}
        overflow="hidden"
      >
        {hasChildren && (
          <CellExpanderFormatter
            isCellSelected={isCellSelected}
            expanded={row.isExpanded === true}
            onCellExpand={() => toggleSubRow(row.id, row.values)}
          />
        )}

        <RowTypography
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100% - 40px)',
            marginLeft: '8px',
            marginRight: '4px',
          }}
        >
          {isClickable ? (
            <Link to={linkPath} state={row}>
              {displayValue}
            </Link>
          ) : (
            displayValue
          )}
        </RowTypography>
      </Box>
    </Box>
  );
};

type CostCellProps = {
  row: Row;
  year: any;
};

export const CostCell = ({ row, year }: CostCellProps) => {
  let formattedCost;
  const costValue = parseFloat(row[year]?.toString() || '0');
  if (costValue >= 1000000) {
    formattedCost = `${formatNumber((costValue / 1000000))}MM`;
  } else if (costValue >= 1000) {
    formattedCost = `${formatNumber((costValue / 1000))}k`;
  } else {
    formattedCost = formatNumber(costValue);
  }
  return <Box textAlign='right'>${formattedCost}</Box>;
}

const costFormatter = (year: any) => {
  return ({ row }: { row: Row }) => (
    <CostCell row={row} year={year} />
  );
};

export const TotalCostCell = ({ row }: { row: Row; }) => {
  let formattedCost;
  if (row.totalCost >= 1000000) {
    formattedCost = `${formatNumber((row.totalCost / 1000000))}MM`;
  } else if (row.totalCost >= 1000) {
    formattedCost = `${formatNumber((row.totalCost / 1000))}k`;
  } else {
    formattedCost = formatNumber(row.totalCost);
  }
  return <Box textAlign='right'>${formattedCost}</Box>;
};

const totalCostFormatter = () => {
  return ({ row }: { row: Row }) => (
    <TotalCostCell row={row} />
  );
};

export const HierarchyRender = ({ items }: { items: any[]; }) => {
  return <HierarchyWidget items={items} />;
};

const hierarchyRender = (items: any[]) => {
  return () => <HierarchyRender items={items} />;
};

type SimpleHierarchyCellProps = {
  row: any;
  isCellSelected: boolean;
  mainColumn: any;
  toggleSubRow: (id: any) => void;
};

export const SimpleHierarchyCell = ({
  row,
  isCellSelected,
  mainColumn,
  toggleSubRow,
}: SimpleHierarchyCellProps) => {
  const hasChildren = Array.isArray(row.values) && row.values.length > 0;
  const indentLevel = row.level || 0;
  const indentSpace = indentLevel * 16;

  return (
    <Box display="flex">
      <Box display="flex" alignItems="center" ml={`${indentSpace}px`} flexGrow={1} overflow="hidden">
        {hasChildren && (
          <CellExpanderFormatter
            isCellSelected={isCellSelected}
            expanded={row.isExpanded === true}
            onCellExpand={() => {
              return toggleSubRow(row.id);
            }}
          />
        )}
        <RowTypography
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100% - 40px)',
            marginLeft: "8px",
            marginRight: "4px"
          }}
        >
          {row.name || row[mainColumn.name]}
        </RowTypography>
      </Box>
    </Box>
  );
};

const simpleHierarchyFormatter = (
  mainColumn: any,
  detailedRef: any
) => {
  return ({ row, isCellSelected }: any) => (
    <SimpleHierarchyCell
      row={row}
      isCellSelected={isCellSelected}
      mainColumn={mainColumn}
      toggleSubRow={(id) => detailedRef.current.toggleSubRow(id)}
    />
  );
};

type ProgressSectionProps = {
  showTableView: boolean;
  setShowTableView: React.Dispatch<React.SetStateAction<boolean>>;
  renderCostTableView: () => React.ReactNode;
  renderCostProgressChart: () => React.ReactNode;
  colors: any;
};

const ProgressSection = ({
  showTableView,
  setShowTableView,
  renderCostTableView,
  renderCostProgressChart,
  colors
}: ProgressSectionProps) => {
  return (
    <Box sx={{ mt: 2, px: 3 }} id="top-programs-cost">
      <Box
        sx={{
          position: 'relative',
          bgcolor: `${colors.widget.background.card} !important`,
          borderRadius: 2,
        }}
      >
        {/* Toggle code */}
        <Box
          sx={{
            position: { xs: 'static', sm: 'absolute' },
            top: { sm: 16 },
            right: { sm: 24 },
            zIndex: 2,
            display: 'flex',
            flexDirection: 'row',
            gap: { xs: 1, sm: 3 },
            mb: { xs: 1, sm: 0 },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: showTableView ? colors.widget.tab.inActive.color : colors.widget.tab.active.color,
              cursor: "pointer",
              fontWeight: 500,
              borderBottom: showTableView ? "none" : `4px solid ${colors.widget.tab.active.color}`,
              pb: 0.5,
              transition: "color 0.2s, border-bottom 0.2s"
            }}
            onClick={() => setShowTableView(false)}
          >
            Chart
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: showTableView ? colors.widget.tab.active.color : colors.widget.tab.inActive.color,
              cursor: "pointer",
              fontWeight: 500,
              borderBottom: showTableView ? `4px solid ${colors.widget.tab.active.color}` : "none",
              pb: 0.5,
              transition: "color 0.2s, border-bottom 0.2s"
            }}
            onClick={() => setShowTableView(true)}
          >
            Detailed View
          </Typography>
        </Box>
        {/* Chart or Table */}
        <Box>
          {showTableView ? renderCostTableView() : renderCostProgressChart()}
        </Box>
      </Box>
    </Box>
  );
};

interface ResourceCostSummaryProps {
  initialTab?: number;
}

export const queryPreferenceOptions = [
  {
    label: 'Flat List of Programs',
    value: 'flatlist_programname'
  },
  {
    label: 'Tree List of Programs',
    value: 'treelist_programname'
  },
  {
    label: 'Flat List of Sectors',
    value: 'flatlist_sector'
  },
  {
    label: 'Tree List of Sectors',
    value: 'treelist_sector'
  },
  {
    label: 'Flat List of Projects',
    value: 'flatlist_project'
  },
  {
    label: 'Tree List of Projects',
    value: 'treelist_project'
  },
  {
    label: 'Flat List of Consumed Services',
    value: 'flatlist_consumedservice'
  },
];

const getCostSummaryTabValueFromPath = (pathname: string) => {
  if (pathname.includes('/finops/resourcemapping/costsummary/sector')) return 1;
  if (pathname.includes('/finops/resourcemapping/costsummary/program')) return 2;
  if (pathname.includes('/finops/resourcemapping/costsummary/project')) return 3;
  return 0;
};

const ResourceCostSummary: React.FC<ResourceCostSummaryProps> = ({ initialTab = 0 }) => {
  const theme = useTheme();
  const colors = theme.palette as any;
  const { setRightContextContent } = useHeader();
  const location = useLocation();
  const resolvedInitialTab = getCostSummaryTabValueFromPath(location.pathname) || initialTab;
  const [tabValue, setTabValue] = useState<number>(resolvedInitialTab);
  const {
    filterMonth: contextFilterMonth, setFilterMonth,
    timezone, setTimezone,
    filterCustomStartDate, setFilterCustomStartDate,
    filterCustomEndDate, setFilterCustomEndDate,
    setHasFilters,
    setOnFiltersOpen
  } = useFinOpsTab();
  const [noData, setNoData] = useState(true);
  const [showTableView, setShowTableView] = useState(false);
  const [, setSettingsDrawerOpen] = useState(false);
  const [cloudCostBarChart, setCloudCostBarChart] = useState<any>()
  const [cloudCostBarChartData, setCloudCostBarChartData] = useState<SeriesData[]>([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [costBarChart, setCostBarChart] = useState<any>()
  const [costBarChartData, setCostBarChartData] = useState<SeriesData[]>([]);
  const [costProgressChartData, setCostProgressChartData] = useState<any>([]);
  const [costProgressEnvNames, setCostProgressEnvNames] = useState<any>([]);
  const [costProgressEnvSeriesData, setCostProgressEnvSeriesData] = useState<any>([]);
  const [cloudCostTechBarChart, setCloudCostTechBarChart] = useState<any>()
  const [cloudCostTechBarChartData, setCloudCostTechBarChartData] = useState<SeriesData[]>([]);
  const [costByTechBarChart, setCostByTechBarChart] = useState<any>()
  const [costByTechBarChartData, setCostByTechBarChartData] = useState<SeriesData[]>([]);
  const [costProgressAxisMax, setCostProgressAxisMax] = useState<number | undefined>(undefined);
  const [isReapplied, setIsReapplied] = useState(false);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isChartDetailedView, setIsChartDetailedView] = useState(false);
  const [detailedViewTitle, setDetailedViewTitle] = useState('Executive Cost');
  const [detailedViewSubTitle, setDetailedViewSubTitle] = useState('Cost');
  const [detailedViewData, setDetailedViewData] = useState<any>(null);
  // Loading states for individual components
  const [costTrendLoading, setCostTrendLoading] = useState<boolean>(false);
  const [azureCostLoading, setAzureCostLoading] = useState<boolean>(false);
  const [costProgressLoading, setCostProgressLoading] = useState<boolean>(false);
  const [azureCostTechLoading, setAzureCostTechLoading] = useState<boolean>(false);
  const [costTrendByTechLoading, setCostTrendByTechLoading] = useState<boolean>(false);

  //Sector & Date Filter Components
  const [sectorName, setSectorName] = useState('all');
  const [sectorOptionsList, setSectorOptionsList] = useState<string[]>([]);
  const rawFilterMonth = contextFilterMonth;
  const toApiMonth = (val: string) => {
    const map: Record<string, string> = {};
    return map[val] ?? val;
  };
  const filterMonth = toApiMonth(rawFilterMonth);
  const customStartDate = rawFilterMonth === 'custom' ? filterCustomStartDate : undefined;
  const customEndDate = rawFilterMonth === 'custom' ? filterCustomEndDate : undefined;

  const handleSectorChange = async (value: string) => {
    if (!isReapplied) getCostSummaryDetails(value);
  };

  useEffect(() => { setHasFilters(sectorName !== 'all'); }, [sectorName]);
  useEffect(() => { setOnFiltersOpen(() => setSettingsDrawerOpen(true)); }, []);
  useEffect(() => {
    if (!isReapplied) getCostSummaryDetails(sectorName, filterMonth);
  }, [rawFilterMonth, filterCustomStartDate, filterCustomEndDate]);

  const [excludeSeries, setExcludeSeries] = useState<string[]>([]);
  const [searchTextByChart, setSearchTextByChart] = useState('');
  const [isFirstWidget, setIsFirstWidget] = useState(true);
  const detailedRef = useRef<any>(null);
  const [detailedTableType, setDetailedTableType] = useState('executive-program');

  // Add state for the toggle
  const [costTrendToggle, setCostTrendToggle] = useState<CostTrendToggle>(resolvedInitialTab === 1 ? 'sector' : resolvedInitialTab === 3 ? 'project' : 'program');
  const [customCostTrendToggle, setCustomCostTrendToggle] = useState<'technology' | 'resourcetype' | 'environment'>('technology');
  const [visibleAzureSeries, setVisibleAzureSeries] = useState<string[]>([]);
  const [visibleTrendSeries, setVisibleTrendSeries] = useState<string[]>([]);
  const [widget1Loading, setWidget1Loading] = useState(false);
  const [widget2Loading, setWidget2Loading] = useState(false);

  // Cost Table Data Components
  const [gridWidth, setGridWidth] = useState(window.innerWidth);
  const [years, setYears] = useState<any[]>([]);
  const [detailYears, setDetailYears] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [costTableList, setCostTableList] = useState<Row[]>([]);
  const costExpandList = useRef<Row[]>([]);
  const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
  const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  let defaultColumnNames = [
    'Programs',
    'Sectors',
    'Projects',
    'Resource Types',
    'Consumed Services',
    'Environments',
    'Total'
  ];

  const columnNames = [
    { name: 'programname', label: 'Programs', isEditable: false },
    { name: 'sector', label: 'Sectors', isEditable: false },
    { name: 'project', label: 'Projects', isEditable: false },
    { name: 'resourcetype', label: 'Resource Types', isEditable: false },
    { name: 'consumedservice', label: 'Consumed Services', isEditable: false },
    { name: 'environment', label: 'Environments', isEditable: false },
    { name: 'totalCost', label: 'Total', isEditable: false },
  ];

  // Advance Query Builder
  const getInitialQuery = (tabValue: number) => ({
    id: '660802c2-f66d-46ed-a996-24962e922680',
    rules: [
      { field: tabValue === 1 ? 'Sector' : 'Program', operator: '=', value: 'All' },
      'and',
      { field: 'Project', operator: '=', value: 'All' }
    ]
  });

  const [isRunQuery, setIsRunQuery] = useState(false);
  const [query, setQuery] = useState<any>(getInitialQuery(0));
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isAbort, setIsAbort] = useState(false);
  const [keyFields, setKeyFields] = useState({});
  type SqlQuery = { sql: string; params: unknown[] };
  const [queryProps, setQueryProps] = useState<{
    isSearch: boolean;
    query: string | SqlQuery;
  }>({
    isSearch: false,
    query: ""
  });
  const [queryPreference, setQueryPreference] = useState('flatlist_programname');

  const fetchKeyFieldsData = async () => {
    try {
      const result = [
        'Program',
        'Project',
        'Sector',
        'ResourceType',
        'ConsumedService',
        'MeterCategory',
        'Environment',
        'Subscription',
        'ResourceGroup',
        'Resource',
        'Year',
        'Month'
      ]
      if (result) {
        const keyFieldRes = result;
        const fieldOptions = {
          autocomplete: [
            'Program',
            'Project',
            'Sector',
            'ResourceType',
            'ConsumedService',
            'MeterCategory',
            'Environment',
            'Subscription',
            'ResourceGroup',
            'Resource',
            'Year',
            'Month'
          ],
          textfield: [''],
          boolean: ['']
        };
        const fields: any = await Promise.all(
          keyFieldRes.map(async (field: string) => {
            let fieldType;
            if (field === 'Year' || field === 'Month') {
              fieldType = inputFieldType.number;
            } else if (fieldOptions.autocomplete.includes(field)) {
              fieldType = inputFieldType.autocomplete;
            } else if (fieldOptions.textfield.includes(field)) {
              fieldType = inputFieldType.textfield;
            } else {
              fieldType = inputFieldType.autocomplete;
            }

            let operators;
            if (field === 'Year' || field === 'Month') {
              operators = getOperators('number');
            } else if (fieldOptions.boolean?.includes(field)) {
              operators = getOperators('boolean');
            } else {
              operators = getOperators('string');
            }

            let valueType: string;
            if (field === 'Year' || field === 'Month') {
              valueType = 'number';
            } else if (fieldOptions.boolean?.includes(field)) {
              valueType = 'boolean';
            } else {
              valueType = 'string';
            }

            return {
              [field]: {
                name: field,
                label: field.replace(/([a-z])([A-Z][a-z]+)$/, '$1 $2').trim(),
                fieldOptions: {
                  fieldInput: fieldType,
                  options: null,
                  operators: operators,
                  valueType: valueType
                }
              }
            };
          })
        );
        return fields.reduce((acc: any, field: any) => ({ ...acc, ...field }), {});
      }
    } catch (error) {
      console.error('Error fetching fields:', error);
      return {};
    }
  };

  const fetchFields = async () => {
    setLoading(true);
    const fieldsData = await fetchKeyFieldsData();
    setKeyFields(fieldsData);
    setLoading(false);
  };

  useEffect(() => {
    if (isReapplied) {
      clearSearchData();
      setNoData(true);
    }
  }, [isReapplied]);

  const getYearAndTotalColumns = () => {
    // 3. Add dynamic year columns
    const yearColumnsDefs = years.map(year => {
      if (!defaultColumnNames.includes(year)) {
        defaultColumnNames.push(year);
      }
      return {
        key: year,
        name: year,
        label: year,
        resizable: true,
        maxWidth: 100,
        formatter: costFormatter(year),
      };
    });

    // 4. Add Total column
    const totalColDef = {
      key: 'totalCost',
      name: 'Total',
      label: 'Total',
      maxWidth: 100,
      resizable: true,
      formatter: totalCostFormatter(),
    };

    // 5. Ensure 'Total' is last in defaultColumnNames
    const totalIndex = defaultColumnNames.indexOf('Total');
    if (totalIndex > -1) {
      defaultColumnNames.splice(totalIndex, 1);
    }
    defaultColumnNames.push('Total');

    return { yearColumnsDefs, totalColDef };
  };

  const handleGetDetailedTableData = async (pageNumber: any, size: any, sortingInfo: any) => {
    setDetailLoading(true);
    try {
      const res = await getChartDetailViewData({ pageNumber, size, sortingInfo, isFirstWidgetFlg: isFirstWidget });
      setDetailedViewData(res);
      setIsChartDetailedView(true);
    } catch (err) {
      console.error(err);
      if (typeof err === 'object' && err !== null && 'code' in err && (err as any).code === 'ERR_CANCELED') {
        console.warn('Request was canceled by user');
        eventEmitter?.emit('showSnackbar', 'Request was canceled', 'info');
      }
    } finally {
      setDetailLoading(false);
    }
  };

  async function getChartDetailViewData({
    pageNumber,
    size,
    sortingInfo,
    isFirstWidgetFlg,
    seriesName = '',
    excludeSeriesData = []
  }: any) {
    try {
      if (100 !== size) {
        pageNumber = 1;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current?.signal;
      const dateFilter = filterMonth;
      const viewType = queryPreference;

      const searchFilter = resolveSearchFilter(isFirstWidgetFlg, costTrendToggle, customCostTrendToggle);

      const rawQuery = typeof queryProps.query === 'object' ? queryProps.query : { sql: '', params: [] };
      const query = (typeof normalizeQuerySql === 'function') ? normalizeQuerySql(rawQuery) : rawQuery;

      const searchTextChart = resolveSearchText(seriesName, searchTextByChart);

      const detailFilter: any = { searchFilter, searchText: searchTextChart };
      if ((Array.isArray(excludeSeriesData) && excludeSeriesData.length) || (Array.isArray(excludeSeries) && excludeSeries.length)) {
        detailFilter.excludeSeries = (excludeSeriesData && excludeSeriesData.length > 0)
          ? excludeSeriesData : excludeSeries;
      }

      const res = await fetchResourceCostSummaryKpi({
        page: pageNumber,
        size,
        sortingInfo: sortingInfo,
        searchField: 'detail',
        sqlParams: query.params,
        sql: query.sql,
        viewType,
        dateFilter,
        sectorFilter: sectorName,
        isDetailView: true,
        detailFilter,
        signal
      });

      if (res?.success) {
        return { data: res.data };
      }

      return { data: [] };
    } catch (error) {
      handleApiError(error);
      return { data: [] };
    }
  }


  async function getCostTableData(pageNumber: any, size: any, sortingInfo: any) {
    if (pageSize !== size) {
      pageNumber = 1;
    }
    setTableLoading(true);
    const dateFilter = filterMonth;
    const viewType = queryPreference;
    let searchText = 'executive';
    if (tabValue === 1) {
      searchText = 'sector';
    } else if (tabValue === 2) {
      searchText = 'program';
    } else if (tabValue === 3) {
      searchText = 'project';
    }
    const query = typeof queryProps.query === 'object' ? queryProps.query : { sql: '', params: [] };
    const res = await fetchResourceCostSummaryKpi({
      page: pageNumber,
      size,
      totalPages: totalPages,
      sortingInfo: sortingInfo,
      searchField: 'detail',
      sqlParams: query.params,
      sql: query.sql,
      viewType,
      searchText,
      dateFilter,
      sectorFilter: sectorName,
      customStartDate,
      customEndDate,
    });
    if (res.success) {
      getTableCostData(res, pageNumber, size, sortingInfo);
      setTableLoading(false);
    }
  }

  // Export Table Data
  const exportResourceCostSummary = async () => {
    let formatType = "EXCEL";
    const dateFilter = filterMonth;
    const viewType = queryPreference;
    const groupBy = queryPreference.split('_')[1] || 'programname';
    const mainColumn = columnNames.find(
      (col) => col.name.toLowerCase() === groupBy.toLowerCase()
    ) || columnNames[0];

    // Export columns: label for main column, years, and 'Total'
    let exportColumns: string[];
    if (groupBy === 'sector') {
      exportColumns = [
        'Sectors',
        'Programs',
        'Projects',
        'ResourceTypes',
        'ConsumedServices',
        ...years,
        'Total'
      ];
    } else if (groupBy === 'programname') {
      exportColumns = [
        'Programs',
        'Projects',
        'ResourceTypes',
        'ConsumedServices',
        ...years,
        'Total'
      ];
    } else if (groupBy === 'project') {
      exportColumns = [
        'Projects',
        'ResourceTypes',
        'ConsumedServices',
        ...years,
        'Total'
      ];
    } else {
      exportColumns = [mainColumn.label, ...years, 'Total'];
    }
    let searchText = 'executive';
    if (tabValue === 1) {
      searchText = 'sector';
    } else if (tabValue === 2) {
      searchText = 'program';
    } else if (tabValue === 3) {
      searchText = 'project';
    }
    const query = typeof queryProps.query === 'object' ? queryProps.query : { sql: '', params: [] };
    const requestBody = {
      formatType,
      exportColumns,
      page: 1,
      pageSize: 100000,
      totalPages: 1,
      sortingInfo,
      searchField: 'export',
      sqlParams: query.params,
      sql: query.sql,
      viewType,
      searchText,
      dateFilter,
      sectorFilter: sectorName,
    }
    await exportCostSummaryTableData(requestBody);
  };

  const fetchFieldOptions = async (fieldKey: string) => {
    try {
      const fieldsRes = await fetchResourceCostSummaryKpi({ searchField: fieldKey, searchQuery: queryPreference });
      if (fieldsRes.success) {
        const options = fieldsRes.data.result.map((x: any) => {
          return x === null ? '' : x;
        });
        setKeyFields((prevFields: any) => {
          const existing = prevFields[fieldKey] ?? {};
          const existingFieldOptions = existing.fieldOptions ?? {};
          return {
            ...prevFields,
            [fieldKey]: {
              ...existing,
              fieldOptions: { ...existingFieldOptions, options }
            }
          };
        });
        return options;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchFilterOptions = async (fieldKey: string) => {
    try {
      const fieldsRes = await fetchResourceCostSummaryKpi({ searchField: fieldKey, searchQuery: queryPreference });
      if (fieldsRes.success) {
        const sectorOptions = fieldsRes.data.result.filter((x: string) => x !== "All").sort();
        setSectorOptionsList(sectorOptions);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Table Data Components
  const getTableCostData = (result: any, pageNumber: any, size: any, sortingInfo: any) => {
    setCostTableList(result.data.data);
    costExpandList.current = result.data.data;
    setPaginationData(result.data.page);
    setPage(pageNumber);
    setPageSize(size);
    setSortingInfo(sortingInfo);
    const uniqueYears = new Set<string>();
    for (const item of result.data.data) {
      for (const key of Object.keys(item)) {
        if (
          key !== 'programname' && key !== 'sector' &&
          key !== 'project' && key !== 'consumedservice' &&
          key !== 'totalCost' && key !== 'id' &&
          key !== 'name' && key !== 'values' &&
          key !== 'level' && key !== 'label' &&
          key !== 'programId' && key !== 'projectId' &&
          key !== 'environment'
        ) {
          uniqueYears.add(key);
        }
      }
    }
    const yearColumns = Array.from(uniqueYears).sort(((a, b) => a.localeCompare(b)));
    setYears(yearColumns);
  };

  // Function to fetch or process data for the progress chart
  const getProgressChartData = (result: any) => {
    // Fetch or process data for the chart
    const programCostData = result || [];
    // Extract unique environment names from the data
    const envNames = programCostData.length > 0
      ? Object.keys(programCostData[0]).filter((key) => key !== 'programname' && key !== 'total_cost')
      : [];

    const totalCosts = programCostData.map((item: any) => Number(item.total_cost) || 0);
    const scales = programCostData.map((item: any) => {
      const sumEnv = envNames.reduce((s: number, env: string) => s + (Number(item[env]) || 0), 0);
      return sumEnv > 0 ? (Number(item.total_cost || 0) / sumEnv) : 0;
    });

    // Create a series for the environment data
    const envSeriesData = envNames.map((env, index) => ({
      name: env.toUpperCase(),
      type: 'bar',
      stack: 'total',
      showBackground: true,
      backgroundStyle: {
        color: colors.widget.background.chart
      },
      label: {
        show: index === envNames.length - 1,
        position: 'right',
        color: colors.widget.text.main,
        clip: false,
        formatter: (params: any) => {
          const costIdx = programCostData.length - 1 - params.dataIndex;
          const totalCost = programCostData[costIdx].total_cost;
          let formattedValue;
          if (totalCost >= 1000000) {
            formattedValue = `${formatNumber(totalCost / 1000000)}MM`;
          } else {
            formattedValue = `${formatNumber(totalCost / 1000)}K`;
          }
          return `$${formattedValue}`;
        }
      },
      data: programCostData.map((item: any, rowIdx: number) => {
        const rawEnv = Number(item[env] || 0);
        const scaled = rawEnv * (scales[rowIdx] ?? 0);
        return scaled;
      }).reverse()
    }));

    const totalCostSeries = {
      name: 'TOTAL_COST',
      type: 'bar',
      stack: 'total',
      label: {
        show: false,
        position: 'right',
        color: colors.widget.text.main,
        formatter: (params: any) => {
          const costIdx = programCostData.length - 1 - params.dataIndex;
          const totalCost = programCostData[costIdx].total_cost;
          let formattedValue;
          if (totalCost >= 1000000) {
            formattedValue = `${formatNumber(totalCost / 1000000)}MM`;
          } else {
            formattedValue = `${formatNumber(totalCost / 1000)}K`;
          }
          return `$${formattedValue}`;
        }
      },
      // Make the bar invisible but clickable
      itemStyle: {
        color: 'transparent' // Invisible bar
      },
      emphasis: {
        itemStyle: {
          color: 'transparent'
        }
      },
      data: totalCosts.reverse()
    };

    const maxTotalCost = totalCosts.length ? Math.max(...totalCosts) : 0;
    const localMaxTotalCost = Math.ceil(maxTotalCost * 1.15 || 0);

    setCostProgressEnvNames(envNames);
    setCostProgressChartData(programCostData);
    setCostProgressEnvSeriesData(programCostData.length > 0 ? [...envSeriesData, totalCostSeries] : []);
    setCostProgressAxisMax(localMaxTotalCost);
    setCostProgressLoading(false);
  };

  const getSectorProjectChartData = (result: any) => {
    // array format
    const costChartData = (result || []).map((item: any) => ({
      axisName: (tabValue === 1 ? item.sector : item.project),
      cost: item.cost
    }));
    const envNames = costChartData.map((d: any) => d.axisName);
    const seriesData = [
      {
        name: "Cost",
        type: "bar",
        data: costChartData.map((d: any) => d.cost).reverse(),
        itemStyle: { color: colors.widget.colors.color8, },
        showBackground: true,
        backgroundStyle: { color: colors.widget.background.chart, },
        label: {
          show: true,
          position: 'right',
          color: colors.widget.text.main,
          clip: false,
          formatter: (params: any) => {
            const cost = params.value;
            let formattedValue;
            if (cost >= 1000000) {
              formattedValue = `${formatNumber(cost / 1000000)}MM`;
            } else {
              formattedValue = `${formatNumber(cost / 1000)}K`;
            }
            return `$${formattedValue}`;
          }
        }
      }
    ];
    setCostProgressEnvNames(envNames);
    setCostProgressChartData(costChartData);
    setCostProgressEnvSeriesData(seriesData);
    setCostProgressAxisMax(undefined);
    setCostProgressLoading(false);
  }

  const getBarChartData = (result: any, byTechnology: boolean = false) => {
    const costTrendData = result.data.chartData;
    const arrayData = result?.data?.chartData ? Object.entries(result.data.chartData).map(([key, value]) => ({
      [key]: value
    })) : [];
    // Extract all the months and teams
    const months = Object.keys(costTrendData);
    let teams: any[] = [];
    if (costTrendData?.[months[0]]) {
      for (const month of months) {
        const monthTeams = Object.keys(result.data.chartData[month]).filter(key => key !== 'TotalCost');
        for (const team of monthTeams) {
          if (!teams.includes(team)) {
            teams.push(team);
          }
        }
      }
    }
    const othersVal = teams.filter(team => team === 'Others');
    teams = teams.filter(team => team !== 'Others');
    if (othersVal)
      teams = teams.concat(othersVal);

    const costSeriesData = teams.map((team, index) => {
      const truncated = team.length > 30 ? team.substring(0, 30) + '...' : team;
      return {
        name: team, // full name for dataKey and tooltip
        truncatedName: truncated, // for legend display
        dataKey: team,
        color: seriesColor[index % seriesColor.length],
      };
    });

    if (byTechnology) {
      setCostByTechBarChartData(costSeriesData);
      setVisibleTrendSeries(costSeriesData.map(s => s.name));
      setCostByTechBarChart(arrayData);
      setCostTrendByTechLoading(false);
    } else {
      setCostBarChartData(costSeriesData);
      setVisibleAzureSeries(costSeriesData.map(s => s.name));
      setCostBarChart(arrayData);
      setCostTrendLoading(false);
    }
  };

  const getCloudCostBarChartData = (result: any, byTechnology: boolean = false) => {
    const costTrendData = result.data.yearlyChartData;
    const arrayData = result?.data?.yearlyChartData ? Object.entries(result.data.yearlyChartData).map(([key, value]) => ({
      [key]: value
    })) : [];
    // Extract all the months and teams
    const months = Object.keys(costTrendData);
    let teams: any[] = [];
    if (costTrendData?.[months[0]]) {
      for (const month of months) {
        const monthTeams = Object.keys(result.data.yearlyChartData[month]).filter(key => key !== 'TotalCost');
        for (const team of monthTeams) {
          if (!teams.includes(team)) {
            teams.push(team);
          }
        }
      }
    }
    const othersVal = teams.filter(team => team === 'Others');
    teams = teams.filter(team => team !== 'Others');
    if (othersVal)
      teams = teams.concat(othersVal);

    const costSeriesData = teams.map((team, index) => {
      const truncated = team.length > 30 ? team.substring(0, 30) + '...' : team;
      return {
        name: team, // full name for dataKey and tooltip
        truncatedName: truncated, // for legend display
        dataKey: team,
        color: seriesColor[index % seriesColor.length],
      };
    });

    if (byTechnology) {
      setCloudCostTechBarChartData(costSeriesData);
      setVisibleTrendSeries(costSeriesData.map(s => s.name));
      setCloudCostTechBarChart(arrayData);
      setAzureCostTechLoading(false);
    } else {
      setCloudCostBarChartData(costSeriesData);
      setVisibleAzureSeries(costSeriesData.map(s => s.name));
      setCloudCostBarChart(arrayData);
      setAzureCostLoading(false);
    }
  };

  const clearSearchData = () => {
    // Cloud Cost Bar Chart
    setVisibleAzureSeries([]);
    setCloudCostBarChart('');
    setCloudCostBarChartData([]);
    setAzureCostLoading(false);

    // Bar Chart
    setCostBarChartData([]);
    setCostBarChart('');
    setCostTrendLoading(false);

    // Technology Vertical Bar Chart
    setVisibleTrendSeries([]);
    setCloudCostTechBarChart('');
    setCloudCostTechBarChartData([]);
    setAzureCostTechLoading(false);

    // Top Chart
    setCostProgressEnvNames([]);
    setCostProgressChartData([]);
    setCostProgressEnvSeriesData([]);
    setCostProgressLoading(false);

    // Detail Table
    setCostTableList([]);
    setPaginationData({ totalElements: 0, totalPages: 0 });
    setPage(1);
    setPageSize(100);
    setTotalPages(0);
  };

  useEffect(() => {
    const resolvedTabValue = getCostSummaryTabValueFromPath(location.pathname) || initialTab;
    clearSearchData();
    setCostTrendLoading(true);
    setAzureCostLoading(true);
    setCostProgressLoading(true);
    setTabValue(resolvedTabValue);
    setQuery(getInitialQuery(resolvedTabValue));
    setCostTrendToggle(resolvedTabValue === 1 ? 'sector' : resolvedTabValue === 3 ? 'project' : 'program');
    setIsInitialLoad(true);
    setIsRunQuery(false);
    setExcludeSeries([]);
  }, [location.pathname, initialTab]);

  useEffect(() => {
    setRightContextContent(
      <Box display="flex" alignItems="center" sx={{ pb: 1, zIndex: 111 }}>
        {/* <GenericMonthRangeFilter
            customMonth={filterCustomStartDate as unknown as string}
            filterMonth={contextFilterMonth}
            setCustomMonth={(value: string) => setFilterCustomStartDate(value as unknown as Date)}
            setFilterMonth={setFilterMonth}
            handleChange={handleMonthRangeChange}
            isRMCost
            disableFilter={detailLoading || isChartDetailedView}
          /> */}
        <SingleSelectFilter
          sectorName={sectorName}
          setSectorName={setSectorName}
          sectorOptionsList={sectorOptionsList}
          handleChange={handleSectorChange}
        />
      </Box>,
    );

    return () => {
      setRightContextContent(null);
    };
  }, [sectorName, sectorOptionsList, detailLoading, isChartDetailedView]);

  function isEmptyChartData(res: any) {
    if (!res?.data) return true;

    const hasYearly =
      res.data.yearlyChartData &&
      Object.keys(res.data.yearlyChartData).length > 0;

    const hasChart =
      res.data.chartData &&
      Object.keys(res.data.chartData).length > 0;

    return !hasYearly && !hasChart;
  }

  function initAbortAndRequestId(ref: any, requestIdRef: any) {
    requestIdRef.current += 1;

    if (ref.current) {
      ref.current.abort();
    }

    ref.current = new AbortController();
    return {
      signal: ref.current.signal,
      requestId: requestIdRef.current
    };
  }

  function resolveSearchTextValue() {
    switch (tabValue) {
      case 1:
        return 'sector';
      case 2:
        return 'program';
      case 3:
        return 'project';
      default:
        return 'executive';
    }
  }

  async function fetchAllCostSummaryData({ sector, selectedDate }: { sector?: any, selectedDate?: any }) {
    const signal = abortControllerRef.current?.signal;
    let dateFilter = selectedDate || filterMonth;
    let sectorFilter = sector || sectorName;
    const viewType = queryPreference;
    const query = typeof queryProps.query === 'object' ? queryProps.query : { sql: '', params: [] };
    const searchText = resolveSearchTextValue();
    const searchTextVal = costTrendToggle;
    const searchTextTrendVal = customCostTrendToggle === 'technology' ? 'consumedservice' : customCostTrendToggle;
    return Promise.all([
      fetchResourceCostSummaryKpi({
        searchField: 'azurecloudcost',
        sqlParams: query.params,
        sql: query.sql,
        viewType,
        searchText: searchTextVal,
        dateFilter,
        sectorFilter,
        customStartDate,
        customEndDate,
        signal
      }),
      fetchResourceCostSummaryKpi({
        searchField: 'azurecloudcost',
        sqlParams: query.params,
        sql: query.sql,
        viewType,
        searchText: searchTextTrendVal,
        dateFilter,
        sectorFilter,
        customStartDate,
        customEndDate,
        signal
      }),
      fetchResourceCostSummaryKpi({
        searchField: 'chart',
        sqlParams: query.params,
        sql: query.sql,
        viewType,
        searchText,
        dateFilter,
        sectorFilter,
        customStartDate,
        customEndDate,
        signal
      }),
      fetchResourceCostSummaryKpi({
        page,
        pageSize,
        totalPages,
        sortingInfo,
        searchField: 'detail',
        sqlParams: query.params,
        sql: query.sql,
        viewType,
        searchText,
        dateFilter,
        sectorFilter,
        customStartDate,
        customEndDate,
        signal
      }),
    ]);
  }

  function shouldShowNoData(
    azureProgramRes: any,
    azureTechnologyRes: any,
    progressChartRes: any,
    detailTableRes: any
  ) {
    return (
      (!azureProgramRes.data || ((!azureProgramRes.data?.yearlyChartData ||
        Object.keys(azureProgramRes.data.yearlyChartData).length === 0) &&
        (!azureProgramRes.data?.chartData || Object.keys(azureProgramRes.data.chartData).length === 0)))
      && (!azureTechnologyRes.data || ((!azureTechnologyRes.data?.yearlyChartData ||
        Object.keys(azureTechnologyRes.data.yearlyChartData).length === 0) && (!azureTechnologyRes.data?.chartData ||
          Object.keys(azureTechnologyRes.data.chartData).length === 0)))
      && progressChartRes.data.data.length <= 0
      && (detailTableRes.data.length <= 0 || detailTableRes.data?.data.length <= 0)
    );
  }

  function processCostSummaryData({
    azureProgramRes,
    azureTechnologyRes,
    progressChartRes,
    detailTableRes
  }: any) {
    if (azureProgramRes.data) {
      // Azure Cloud Cost Vertical Bar Chart Component
      getCloudCostBarChartData(azureProgramRes);
      // Cost Trend Bar Chart Component
      getBarChartData(azureProgramRes);
    }
    if (azureTechnologyRes.data) {
      // Azure Cost by Technology Vertical Bar Chart Component
      getCloudCostBarChartData(azureTechnologyRes, true);
      // Cost Trend by Technology Bar Chart Component
      getBarChartData(azureTechnologyRes, true);
    }
    if (progressChartRes.data.data) {
      // Top 10 Programs Cost/Top Projects/Sectors Progress Chart Component
      if (tabValue === 1 || tabValue === 3) getSectorProjectChartData(progressChartRes.data.data);
      else getProgressChartData(progressChartRes.data.data);
    }
    if (detailTableRes.data.data) {
      // Cost Table Data Component
      getTableCostData(detailTableRes, page, pageSize, sortingInfo);
    }
  }

  const requestIdRef = useRef(0);
  async function getCostSummaryDetails(sector?: any, selectedDate?: any) {
    if ((queryProps.query === null || !queryProps.query)) return;
    setIsAbort(true);
    // Increment requestId for each new request
    const currentRequestId = ++requestIdRef.current;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    try {
      setLoading(true);
      //Fetch all required data concurrently
      const [
        azureProgramRes,
        azureTechnologyRes,
        progressChartRes,
        detailTableRes
      ] = await fetchAllCostSummaryData({ sector, selectedDate });

      if (
        shouldShowNoData(
          azureProgramRes,
          azureTechnologyRes,
          progressChartRes,
          detailTableRes
        )
      ) {
        setNoData(true);
      } else {
        setNoData(false);
        processCostSummaryData({
          azureProgramRes,
          azureTechnologyRes,
          progressChartRes,
          detailTableRes,
        });
      }

      // Before updating state, check if this is the latest request
      if (requestIdRef.current !== currentRequestId) return;
    } catch (error) {
      handleApiError(error);
    } finally {
      // Only update loading if this is the latest request
      if (requestIdRef.current === currentRequestId) {
        setLoading(false);
        setIsAbort(false);
      }
    }
  }


  useEffect(() => {
    if (isInitialLoad || (!isInitialLoad && isRunQuery))
      getCostSummaryDetails();
  }, [queryProps, isInitialLoad]);

  // Cost Progress Chart Component
  useEffect(() => {
    if (showTableView) return;
  }, [tabValue]);

  // Cost Table Data Components
  useEffect(() => {
    if (!showTableView) return;
    const handleResize = () => setGridWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showTableView]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);

    const activeId = document.getElementById('root');
    if (activeId) {
      document.head.classList.add('theme-bg-blue');
      document.body.classList.add('theme-bg-blue');
    }
    fetchFields();
    fetchFilterOptions('sector');
    return () => {
      window.removeEventListener('resize', handleResize);
      document.head.classList.remove('theme-bg-blue');
      document.body.classList.remove('theme-bg-blue');
    };
  }, []);

  const normalizeQuerySql = (q: any) => {
    if (!q) return { sql: '', params: [] };
    const out = { ...q };
    if (typeof out.sql === 'string') {
      out.sql = out.sql
        .replaceAll(/\bProgram\b/gi, 'ProgramName')
        .replaceAll(/\bYear\b/gi, 'yearmonth/100')
        .replaceAll(/\bMonth\b/gi, 'yearmonth%100');
    }
    return out;
  };

  function isProgramWidget(type: any) {
    return type === 'program';
  }

  async function fetchCostWidgetData(type: 'program' | 'technology') {
    if ((queryProps.query === null || !queryProps.query)) return;
    const dateFilter = filterMonth;
    const viewType = queryPreference;
    const rawQuery = typeof queryProps.query === 'object' ? queryProps.query : { sql: '', params: [] };
    const query = (typeof normalizeQuerySql === 'function') ? normalizeQuerySql(rawQuery) : rawQuery;

    const isProgram = isProgramWidget(type);
    const textVal = customCostTrendToggle === 'technology' ? 'consumedservice' : customCostTrendToggle;
    const searchText = isProgram ? costTrendToggle : textVal;

    if (isProgram) setWidget1Loading(true);
    else setWidget2Loading(true);

    try {
      const res = await fetchResourceCostSummaryKpi({
        searchField: 'azurecloudcost',
        sqlParams: query.params,
        sql: query.sql,
        viewType,
        searchText: searchText,
        dateFilter,
        sectorFilter: sectorName,
      });

      const isNoData = (!res.data || ((!res.data?.yearlyChartData || Object.keys(res.data.yearlyChartData).length === 0)
        && (!res.data?.chartData || Object.keys(res.data.chartData).length === 0)));

      setNoData(isNoData);

      if (!isNoData && res.data) {
        getCloudCostBarChartData(res, !isProgram);
        getBarChartData(res, !isProgram);
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (isProgram) setWidget1Loading(false);
      else setWidget2Loading(false);
    }
  }


  useEffect(() => {
    fetchCostWidgetData('program');
  }, [costTrendToggle]);

  useEffect(() => {
    fetchCostWidgetData('technology');
  }, [customCostTrendToggle]);

  function getToggleHeader<T extends string>(
    options: { label: string; value: T }[],
    value: T,
    setValue: React.Dispatch<React.SetStateAction<T>>
  ) {
    return (
      <ToggleHeader<T>
        options={options}
        value={value}
        setValue={setValue}
        colors={colors}
      />
    );
  }

  function getCostTrendHeader(type: 'program' | 'technology') {
    if (type === 'program') {
      return getToggleHeader<'program' | 'sector' | 'project'>(
        [
          ...(tabValue === 3
            ? [{ label: "By Project", value: "project" as const }]
            : [{ label: "By Program", value: "program" as const }]),
          { label: "By Sector", value: "sector" as const }
        ],
        costTrendToggle,
        setCostTrendToggle
      );
    } else {
      return getToggleHeader<'technology' | 'resourcetype' | 'environment'>(
        [
          { label: "By Technology", value: "technology" as const },
          { label: "By Resource Type", value: "resourcetype" as const },
          { label: "By Environment", value: "environment" as const }
        ],
        customCostTrendToggle,
        setCustomCostTrendToggle
      );
    }
  }

  const getWidgetHeaderTitle = (type: 'program' | 'technology') => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        flexWrap: 'wrap'
      }}
    >
      <Typography
        sx={{
          color: colors.textLight.main,
          fontWeight: 600
        }}
      >
        Query Results
      </Typography>

      {getCostTrendHeader(type)}
    </Box>
  );

  const handleLegendToggle = (
    seriesName: string,
    setVisible: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setVisible(prev =>
      prev.includes(seriesName)
        ? prev.filter(name => name !== seriesName)
        : [...prev, seriesName]
    );
  };

  const onChartClick = async (params: any, isFirstWidgetFlg: boolean, chartType: string, data?: SeriesData[]) => {
    setDetailLoading(true);
    try {
      const seriesName = params.seriesName ?? '';
      const xAxisName = params.name ?? '';
      setExcludeSeries([]);
      setSearchTextByChart(seriesName);
      setIsFirstWidget(isFirstWidgetFlg);
      if (!seriesName) {
        setDetailLoading(false);
        return;
      }

      let tabName = 'executive';
      if (tabValue === 1) tabName = 'sector';
      if (tabValue === 2) tabName = 'program';
      if (tabValue === 3) tabName = 'project';
      let detailTableType = `${tabName}-${costTrendToggle}-${chartType}`;
      if (!isFirstWidgetFlg) {
        detailTableType = `${tabName}-${customCostTrendToggle}-${chartType}`;
      }
      setDetailedTableType(detailTableType);
      setExcludeSeries([]);
      let excludeSeriesData: string[] = [];
      if (seriesName.toLowerCase() === 'others') {
        const topSeries = (data || [])
          .map(s => s.name)
          .filter(n => n && n.toLowerCase() !== 'others');
        excludeSeriesData = topSeries;
        setExcludeSeries(excludeSeriesData);
      }
      let detailedViewTitle = 'Executive Cost';
      if (tabValue === 1) detailedViewTitle = 'Sector Cost';
      else if (tabValue === 2) detailedViewTitle = 'Program Cost';
      else if (tabValue === 3) detailedViewTitle = 'Project Cost';
      setDetailedViewTitle(detailedViewTitle);

      setDetailedViewSubTitle(`${xAxisName} Cost`);
      const pageNumber = 1;
      const size = 100;
      const sortingInfo = { sortBy: '', sortDir: '' };
      const res = await getChartDetailViewData({
        pageNumber,
        size,
        sortingInfo,
        isFirstWidgetFlg,
        seriesName,
        excludeSeriesData
      });
      setDetailedViewData(res);
      setIsChartDetailedView(true);
    } catch (err) {
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Advance Query Builder
  const renderAdvanceQueryBuilder = () => {
    return (
      <CostQueryBuilder
        fields={keyFields}
        setQueryProps={setQueryProps}
        queryPreference={queryPreference}
        setQueryPreference={setQueryPreference}
        queryPreferenceOptions={queryPreferenceOptions}
        loadOptionsForField={fetchFieldOptions}
        fieldValueOptionsCallback={fetchResourceCostSummaryKpi}
        setTotalPages={setTotalPages}
        sortingInfo={sortingInfo}
        setSortingInfo={setSortingInfo}
        abortControllerRef={abortControllerRef}
        setIsAbort={setIsAbort}
        isAbort={isAbort}
        tabValue={tabValue}
        query={query}
        setQuery={setQuery}
        setNoData={setNoData}
        clearSearchData={clearSearchData}
        isRunQuery={isRunQuery}
        setIsRunQuery={setIsRunQuery}
        isChartDetailedView={isChartDetailedView}
        setIsReapplied={setIsReapplied}
        isReapplied={isReapplied}
        timeRangeFilter={{
          timeRange: contextFilterMonth,
          setTimeRange: setFilterMonth,
          timezone,
          setTimezone,
          customStartDate: filterCustomStartDate,
          setCustomStartDate: setFilterCustomStartDate,
          customEndDate: filterCustomEndDate,
          setCustomEndDate: setFilterCustomEndDate,
        }}
        sectorName={sectorName}
        setSectorName={setSectorName}
      />
    );
  };

  const renderCloudCostBarChart = () => {
    const chartTitle = "YOY Cost";
    const filteredSeries = cloudCostBarChartData.filter(s => visibleAzureSeries.includes(s.name));
    const chartType = 'yoy';

    return (
      <BarChartWidgetNew
        isLoading={azureCostLoading}
        title={chartTitle}
        data={cloudCostBarChart}
        seriesData={filteredSeries}
        handleEvent={(e) => onChartClick(e, true, chartType, filteredSeries)}
        isCost
        isRMCost
        containerProps={{
          sx: {
            height: '300px',
          }
        }}
      >
      </BarChartWidgetNew>
    );
  };

  const renderCostBarChart = () => {
    const chartTitle = "MOM Cost";
    const filteredSeries = costBarChartData.filter(s => visibleAzureSeries.includes(s.name));
    const chartType = 'mom';
    return (
      <BarChartWidgetNew
        isLoading={costTrendLoading}
        title={chartTitle}
        data={costBarChart}
        seriesData={filteredSeries}
        handleEvent={(e) => onChartClick(e, true, chartType, filteredSeries)}
        isCost
        isRMCost
        containerProps={{
          sx: {
            height: '300px',
          }
        }}
      >
      </BarChartWidgetNew>
    );
  };

  // Inline Azure Cloud Cost By Technology Vertical Bar Chart Component
  const renderCloudCostByTechBarChart = () => {
    const chartTitle = "YOY Cost";
    const filteredSeries = cloudCostTechBarChartData.filter(s => visibleTrendSeries.includes(s.name));
    const chartType = 'yoy';

    return (
      <BarChartWidgetNew
        isLoading={azureCostTechLoading}
        title={chartTitle}
        data={cloudCostTechBarChart}
        seriesData={filteredSeries}
        handleEvent={(e) => onChartClick(e, false, chartType, filteredSeries)}
        isCost
        isRMCost
        containerProps={{
          sx: {
            height: '300px',
          }
        }}
      >
      </BarChartWidgetNew>
    );
  };

  // Inline Cost Trend by Technology Bar Chart Component
  const renderCostByTechnologyBarChart = () => {
    const chartTitle = "MOM Cost";
    const filteredSeries = costByTechBarChartData.filter(s => visibleTrendSeries.includes(s.name));
    const chartType = 'mom';

    return (
      <BarChartWidgetNew
        isLoading={costTrendByTechLoading}
        title={chartTitle}
        data={costByTechBarChart}
        seriesData={filteredSeries}
        handleEvent={(e) => onChartClick(e, false, chartType, filteredSeries)}
        isCost
        isRMCost
        containerProps={{
          sx: {
            height: '300px',
          }
        }}
      >
      </BarChartWidgetNew>
    );
  };

  // Inline Cost Progress Chart Component
  const renderCostProgressChart = () => {
    // chartTitle is used to set the title of the chart based on the tab value
    let chartTitle = "Top Programs by Cost";
    if (tabValue === 1) chartTitle = "Top Sectors by Cost";
    else if (tabValue === 3) chartTitle = "Top Projects";
    // chartIndex is used to determine the chart type (bar or progress)
    let chartIndex = 0;
    if (tabValue === 1 || tabValue === 3) chartIndex = 1;
    return (
      <ProgressChartWidgetNew
        title={chartTitle}
        data={costProgressChartData}
        envNames={costProgressEnvNames}
        seriesData={costProgressEnvSeriesData}
        isLoading={costProgressLoading}
        chartByCost={chartIndex}
        axisMax={costProgressAxisMax}
        containerProps={{
          sx: {
            height: '400px',
          }
        }}
      />
    );
  };


  function collapseChildren(childrenToCollapse: Row[], newRows: Row[]) {
    for (const child of childrenToCollapse) {
      // Find the child in the flat list
      const childIndex = newRows.findIndex((r) => r.id === child.id);
      if (childIndex > -1) {
        // If this child is expanded, recursively collapse its children using its own children property
        if (newRows[childIndex].isExpanded && newRows[childIndex].children && newRows[childIndex].children.length > 0) {
          collapseChildren(newRows[childIndex].children, newRows);
        }
        // Remove the child itself from the flat list
        newRows.splice(childIndex, 1);
      }
    }
  }

  async function toggleSubRow(id: string, resourceTypes: any) {
    setCostTableList((prev) => {
      const rows = [...prev];
      const rowIndex = rows.findIndex((r: any) => r.id === id);
      const row = rows[rowIndex];
      const newRows = [...rows];
      let children: Row[] = [];
      let childRows: Row[] = [];

      if (row && !row.isExpanded) {
        // Always get children from the row's values property (your local data)
        if (Array.isArray(row.values) && row.values.length > 0) {
          childRows = row.values;
        }
        children = childRows.map((x: any) => ({
          ...x,
          isChildren: true,
          isExpanded: false,
          children: []
        }));
        newRows.splice(Number(rowIndex) + 1, 0, ...children);
        newRows[rowIndex] = { ...row, isExpanded: true, children };
      } else {
        // Collapse: recursively remove all descendants
        children = row.children ?? [];
        collapseChildren(children, newRows);
        newRows[rowIndex] = { ...row, isExpanded: false, children: [] };
      }
      return newRows;
    });
  }

  const resourceNameFormatter = (mainColumn: any) => {
    const formatter = ({ row, isCellSelected }: any) => (
      <ResourceNameCell
        row={row}
        isCellSelected={isCellSelected}
        queryPreference={queryPreference}
        mainColumn={mainColumn}
        toggleSubRow={toggleSubRow}
      />
    );
    return formatter;
  };

  // Inline Cost Table View Component
  const renderCostTableView = () => {
    let tableTitle = "Top Programs by Cost";
    let tableName = 'executive';
    if (tabValue === 1) {
      tableTitle = "Top Sectors by Cost";
      tableName = "sector";
    }
    else if (tabValue === 2) {
      tableName = "program";
    }
    else if (tabValue === 3) {
      tableTitle = "Top Projects";
      tableName = "project";
    }

    function getColumns(): Array<any> {
      const groupBy = queryPreference.split('_')[1];

      // 1. Find the main column (groupBy)
      const mainColumn = columnNames.find(
        (col) => col.name.toLowerCase() === groupBy.toLowerCase()
      ) || columnNames[0];

      // 2. Build main column (expandable for tree)
      const mainColDef = {
        key: mainColumn.name,
        name: mainColumn.label,
        resizable: true,
        minWidth: 220,
        width: years.length === 1 ? gridWidth - ((years.length + 1) * 188) : gridWidth - ((years.length + 1) * 153),
        formatter: resourceNameFormatter(mainColumn)
      };

      const { yearColumnsDefs, totalColDef } = getYearAndTotalColumns();

      // 6. Return columns in order: main, years, total
      return [mainColDef, ...yearColumnsDefs, totalColDef];
    }

    const handleSorting = (sortingInfo: any) => {
      getCostTableData(page, pageSize, sortingInfo);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
      getCostTableData(value, pageSize, sortingInfo);
    };

    const handlepageSizeChange = (event: any) => {
      getCostTableData(page, event.target.value, sortingInfo);
    };

    return (
      <Box
        sx={{
          bgcolor: colors.widget.background.card,
          borderRadius: 1.5,
          minHeight: 0,
          p: 0,
          position: 'relative',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            pt: 2,
            px: 2,
          }}
        >
          <Typography variant="widgetTitleNew" sx={{ color: `${colors.widget.text.title} !important`, pb: 1 }}>{tableTitle}</Typography>
        </Box>
        {/* Table */}
        <Box sx={{ px: 2, pb: 2 }}>
          {tableLoading ? (
            <LoaderComponent pt={10} />
          ) : (
            <CustomizedDataGrid
              tableColumns={getColumns()}
              tableData={costTableList}
              tableType={`${tableName}-rm-cost-summary`}
              defaultColumnNames={defaultColumnNames}
              onSorting={handleSorting}
              currSorting={sortingInfo}
              page={page}
              pageSize={pageSize}
              paginationData={paginationData}
              listLength={costTableList.length}
              handlepageSizeChange={handlepageSizeChange}
              handlePageChange={handlePageChange}
              isDateRangeFilter={false}
              isDisabled={false}
              showExport={true}
              iconColor={colors.table.moreIcon}
              fileName="resource-cost-summary"
              exportTableData={() => exportResourceCostSummary()}
              title=""
              hierarchyRender={hierarchyRender(getHierarchyList(false))}
            />
          )}
        </Box>
      </Box>
    );
  };

  const getHierarchyList = (isDetailView: boolean) => {
    if (isDetailView) {
      const searchFilter = isFirstWidget ? costTrendToggle : customCostTrendToggle;
      switch (searchFilter.toLowerCase()) {
        case 'sector':
          return ['Sector', 'Program', 'Project', 'Resource Type', 'Consumed Service'];
        case 'project':
          return ['Project', 'Resource Type', 'Consumed Service'];
        case 'resourcetype':
          return ['Resource Type', 'Consumed Service'];
        case 'program':
        default:
          return ['Program', 'Project', 'Resource Type', 'Consumed Service'];
      }
    }
    else {
      const groupBy = (queryPreference || '').split('_')[1] || 'programname';
      switch (groupBy.toLowerCase()) {
        case 'sector':
          return ['Sector', 'Program', 'Project', 'Resource Type', 'Consumed Service'];
        case 'project':
          return ['Project', 'Resource Type', 'Consumed Service'];
        case 'programname':
        default:
          return ['Program', 'Project', 'Resource Type', 'Consumed Service'];
      }
    }
  };

  function getColumns(): Array<any> {
    let searchFilter: string;
    if (isFirstWidget) {
      searchFilter = costTrendToggle === 'program' ? 'programname' : costTrendToggle;
    } else {
      searchFilter = customCostTrendToggle === 'technology' ? 'consumedservice' : customCostTrendToggle;
    }

    // 1. Find the main column (groupBy)
    const mainColumn = columnNames.find(
      (col) => col.name.toLowerCase() === searchFilter.toLowerCase()
    ) || columnNames[0];

    // 2. Build main column (expandable for tree)
    const mainColDef = {
      key: mainColumn.name,
      name: mainColumn.label,
      resizable: true,
      minWidth: 220,
      width: detailYears.length === 1 ? gridWidth - ((detailYears.length + 1) * 188) : gridWidth - ((detailYears.length + 1) * 153),
      formatter: simpleHierarchyFormatter(mainColumn, detailedRef),
    };

    const { yearColumnsDefs, totalColDef } = getYearAndTotalColumns();

    // 6. Return columns in order: main, years, total
    return [mainColDef, ...yearColumnsDefs, totalColDef];
  }

  function resolveSearchFilterExport(): string {
    if (isFirstWidget) {
      return costTrendToggle === 'program' ? 'programname' : costTrendToggle;
    }
    return customCostTrendToggle === 'technology' ? 'consumedservice' : customCostTrendToggle;
  }

  function resolveExportColumns(
    searchFilter: string,
    mainColumnLabel: string
  ): string[] {
    switch (searchFilter) {
      case 'sector':
        return [
          'Sectors',
          'Programs',
          'Projects',
          'ResourceTypes',
          'ConsumedServices',
          ...detailYears,
          'Total',
        ];
      case 'programname':
        return [
          'Programs',
          'Projects',
          'ResourceTypes',
          'ConsumedServices',
          ...detailYears,
          'Total',
        ];
      case 'project':
        return [
          'Projects',
          'ResourceTypes',
          'ConsumedServices',
          ...detailYears,
          'Total',
        ];
      case 'resourcetype':
        return [
          'ResourceTypes',
          'ConsumedServices',
          ...detailYears,
          'Total',
        ];
      default:
        return [mainColumnLabel, ...detailYears, 'Total'];
    }
  }

  // Export Detail View Table Data
  const exportDetailCostSummary = async (sortingInfo: any) => {
    const formatType = 'EXCEL';
    const dateFilter = filterMonth;
    const searchFilter = resolveSearchFilterExport();
    const mainColumn = columnNames.find(
      (col) => col.name.toLowerCase() === searchFilter.toLowerCase()
    ) || columnNames[0];

    // Export columns: label for main column, years, and 'Total'
    const exportColumns = resolveExportColumns(
      searchFilter,
      mainColumn.label,
    );
    const searchText = resolveSearchTextValue();
    const customFilterFallback = customCostTrendToggle === 'technology'
      ? 'consumedservice' : customCostTrendToggle;
    const searchFilterProp = isFirstWidget ? costTrendToggle : customFilterFallback;
    const searchTextChart = searchTextByChart && searchTextByChart !== 'Untagged'
      ? searchTextByChart : '';
    const detailFilter: any = { searchFilter: searchFilterProp, searchText: searchTextChart };
    if (Array.isArray(excludeSeries) && excludeSeries.length) {
      detailFilter.excludeSeries = (excludeSeries && excludeSeries.length > 0)
        ? excludeSeries : [];
    }

    const viewType = queryPreference;
    const query = typeof queryProps.query === 'object' ? queryProps.query : { sql: '', params: [] };
    const requestBody = {
      formatType,
      exportColumns,
      page: 1,
      pageSize: 100000,
      totalPages: 1,
      sortingInfo,
      viewType,
      searchField: 'export',
      sqlParams: query.params,
      sql: query.sql,
      searchText,
      dateFilter,
      sectorFilter: sectorName,
      isDetailView: true,
      detailFilter,
    };
    await exportCostSummaryTableData(requestBody);
  };

  let w1LegendLabel = 'Top Sectors';
  if (costTrendToggle === 'program') {
    w1LegendLabel = 'Top Programs';
  } else if (costTrendToggle === 'project') {
    w1LegendLabel = 'Top Projects';
  }

  let w2LegendLabel = 'Top Environments';
  if (customCostTrendToggle === 'technology') {
    w2LegendLabel = 'Top Technologies';
  } else if (customCostTrendToggle === 'resourcetype') {
    w2LegendLabel = 'Top Resource Types';
  }

  // Precompute tab content to avoid nested ternary in JSX
  const tabContent = noData ? (
    <ShowInfoWidget title="No data" height="60vh" />
  ) : (
    <>
      <WidgetCardContainer bgColor="transparent" title={getWidgetHeaderTitle('program')} sx={{ paddingLeft: 3 }} mainSx={{ minHeight: '450px' }}>
        {widget1Loading ? (
          <LoaderComponent pt={10} />
        ) : (<>
          <Grid container spacing={1.5} sx={{ mt: 2 }}>
            {/* Left Side - Cloud Cost Bar Chart */}
            <Grid item xs={12} md={4} id="azure-cloud-cost">
              {renderCloudCostBarChart()}
            </Grid>
            {/* Right Side - Cost Trend Bar Chart */}
            <Grid item xs={12} md={8} id="cost-trend">
              {renderCostBarChart()}
            </Grid>
          </Grid>
          <SharedLegend
            seriesData={cloudCostBarChartData}
            visibleSeries={visibleAzureSeries}
            onToggle={name => handleLegendToggle(name, setVisibleAzureSeries)}
            legendLabel={w1LegendLabel}
            containerWidth={windowWidth}
            colors={colors}
          />
        </>)}
      </WidgetCardContainer>

      <WidgetCardContainer bgColor="transparent" title={getCostTrendHeader('technology')} sx={{ paddingLeft: 3 }} mainSx={{ mt: 2, minHeight: '450px' }}>
        {widget2Loading ? (
          <LoaderComponent pt={10} />
        ) : (<>
          <Grid container spacing={1.5} sx={{ mt: 2 }}>
            {/* Left Side - Azure Cloud Cost by Technology */}
            <Grid item xs={12} md={4} id="azure-cloud-cost-by-technology">
              {renderCloudCostByTechBarChart()}
            </Grid>
            {/* Right Side - Cost Trend by Technology */}
            <Grid item xs={12} md={8} id="cost-trend-by-technology">
              {renderCostByTechnologyBarChart()}
            </Grid>
          </Grid>
          <SharedLegend
            seriesData={cloudCostTechBarChartData}
            visibleSeries={visibleTrendSeries}
            onToggle={name => handleLegendToggle(name, setVisibleTrendSeries)}
            legendLabel={w2LegendLabel}
            containerWidth={windowWidth}
            colors={colors}
          />
        </>)}
      </WidgetCardContainer>

      {/* Progress Chart / Table - Full Width */}
      <ProgressSection
        showTableView={showTableView}
        setShowTableView={setShowTableView}
        renderCostTableView={renderCostTableView}
        renderCostProgressChart={renderCostProgressChart}
        colors={colors}
      />
    </>
  );

  // Precompute detailed view element to avoid inline ternary in JSX
  const detailedViewElement = isChartDetailedView ? (
    <DetailedViewWidget
      ref={detailedRef}
      fetchData={detailedViewData}
      setYears={setDetailYears}
      detailedTableType={`${detailedTableType}-rm-detailed-view`}
      tableColumns={getColumns()}
      defaultColumnNames={defaultColumnNames}
      getDetailedTableData={handleGetDetailedTableData}
      getHierarchyList={getHierarchyList}
      exportDetailCostSummary={exportDetailCostSummary}
    />
  ) : null;

  return (
    <PageWrapper sx={{ minHeight: '100vh', p: 0, mt: 5 }}>
      {(detailLoading || isChartDetailedView) && (
        <>
          <PageHeader
            icon={DashboardIcon}
            title={[
              {
                text: detailedViewTitle ?? '',
                link: location.pathname,
                onClick: (e: any) => {
                  e?.preventDefault?.();
                  setDetailLoading(false);
                  setIsChartDetailedView(false);
                  setExcludeSeries([]);
                  setIsInitialLoad(false);
                  setIsRunQuery(false);
                  if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                  }
                }
              },
              {
                text: detailedViewSubTitle ?? ''
              }
            ]}
          >
          </PageHeader>
          {detailLoading ? (
            <LoaderComponent pt={10} />
          ) : (
            detailedViewElement
          )}
        </>
      )}
      {!detailLoading && !isChartDetailedView && (
        <TabPanel value={tabValue} index={tabValue}>
          <Grid item xs={12} sx={{ px: 2, mb: 2 }}>
            {renderAdvanceQueryBuilder()}
          </Grid>
          {loading ? <LoaderComponent pt={10} /> : tabContent}
        </TabPanel>
      )}
    </PageWrapper>
  );
};

export default ResourceCostSummary;
